import { useCallback, useEffect, useId, useLayoutEffect, useRef, useState } from "react";
import type { ReactNode } from "react";
import { createPortal } from "react-dom";
import { Ellipsis } from "lucide-react";
import { cn } from "../lib/cn";
import { IconButton } from "./Button";

export interface ActionMenuItem {
  id: string;
  label: ReactNode;
  /** Ícone de 16px antes do rótulo. */
  icon?: ReactNode;
  onSelect: () => void;
  /** Ação destrutiva: texto em `danger`, e vai sempre por último. */
  destructive?: boolean;
  disabled?: boolean;
  /** Linha divisória ANTES deste item. */
  separated?: boolean;
}

export interface ActionMenuProps {
  items: ActionMenuItem[];
  /** Vira o aria-label do botão ("Ações do grupo Ofertas SP"). Obrigatório. */
  label: string;
  /** Gatilho próprio; o padrão é o `IconButton` "…" fantasma. */
  trigger?: (props: { onClick: () => void; "aria-expanded": boolean; "aria-haspopup": "menu"; "aria-controls"?: string }) => ReactNode;
  size?: "sm" | "md";
  align?: "start" | "end";
  className?: string;
}

/**
 * O "…" de ações secundárias (padrão do export: linha de tabela, card de monitorador).
 *
 * Por que existe: uma linha de tabela com cinco botões de texto lado a lado esconde a ação que
 * importa e estreita as colunas de dado até cortar o texto. Aqui as ações secundárias moram num
 * menu; a primária da tela continua visível.
 *
 * Posição FIXA calculada a partir do botão, num portal: dentro de uma tabela com rolagem
 * horizontal, um menu `absolute` seria cortado pelo `overflow` do container — o clique abriria
 * um menu que ninguém vê. Abre para cima quando não cabe embaixo; fecha com Esc, clique fora,
 * rolagem e redimensionamento. Setas ↑/↓ andam entre os itens (WAI-ARIA menu button).
 */
export function ActionMenu({ items, label, trigger, size = "sm", align = "end", className }: ActionMenuProps) {
  const [open, setOpen] = useState(false);
  const [pos, setPos] = useState<{ top: number; left: number; up: boolean } | null>(null);
  const anchorRef = useRef<HTMLSpanElement>(null);
  const menuRef = useRef<HTMLDivElement>(null);
  const menuId = useId();

  const close = useCallback(() => setOpen(false), []);

  useLayoutEffect(() => {
    if (!open || !anchorRef.current) return;
    const r = anchorRef.current.getBoundingClientRect();
    const altura = menuRef.current?.offsetHeight ?? 0;
    const largura = menuRef.current?.offsetWidth ?? 0;
    const up = r.bottom + altura + 8 > window.innerHeight && r.top - altura - 8 > 0;
    const left = align === "end" ? r.right - largura : r.left;
    setPos({
      top: up ? r.top - altura - 6 : r.bottom + 6,
      left: Math.max(8, Math.min(left, window.innerWidth - largura - 8)),
      up,
    });
    menuRef.current?.querySelector<HTMLButtonElement>('[role="menuitem"]:not(:disabled)')?.focus();
  }, [open, align]);

  useEffect(() => {
    if (!open) return;
    const onPointerDown = (event: PointerEvent) => {
      const alvo = event.target as Node;
      if (!menuRef.current?.contains(alvo) && !anchorRef.current?.contains(alvo)) close();
    };
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        close();
        anchorRef.current?.querySelector("button")?.focus();
      }
    };
    document.addEventListener("pointerdown", onPointerDown);
    document.addEventListener("keydown", onKeyDown);
    window.addEventListener("scroll", close, true);
    window.addEventListener("resize", close);
    return () => {
      document.removeEventListener("pointerdown", onPointerDown);
      document.removeEventListener("keydown", onKeyDown);
      window.removeEventListener("scroll", close, true);
      window.removeEventListener("resize", close);
    };
  }, [open, close]);

  const onMenuKeyDown = (event: React.KeyboardEvent<HTMLDivElement>) => {
    if (event.key !== "ArrowDown" && event.key !== "ArrowUp") return;
    event.preventDefault();
    const botoes = [...(menuRef.current?.querySelectorAll<HTMLButtonElement>('[role="menuitem"]:not(:disabled)') ?? [])];
    const atual = botoes.indexOf(document.activeElement as HTMLButtonElement);
    const proximo = event.key === "ArrowDown" ? (atual + 1) % botoes.length : (atual - 1 + botoes.length) % botoes.length;
    botoes[proximo]?.focus();
  };

  const triggerProps = {
    onClick: () => setOpen((v) => !v),
    "aria-expanded": open,
    "aria-haspopup": "menu" as const,
    ...(open ? { "aria-controls": menuId } : {}),
  };

  return (
    <span ref={anchorRef} className={cn("inline-flex", className)}>
      {trigger ? (
        trigger(triggerProps)
      ) : (
        <IconButton icon={<Ellipsis />} label={label} size={size} variant="ghost" {...triggerProps} />
      )}
      {open && typeof document !== "undefined"
        ? createPortal(
            <div
              ref={menuRef}
              id={menuId}
              role="menu"
              aria-label={label}
              onKeyDown={onMenuKeyDown}
              style={pos ? { top: pos.top, left: pos.left } : { top: -9999, left: -9999 }}
              className="fixed z-[60] min-w-48 max-w-72 animate-fade-in rounded-lg bg-surface-raised p-1.5 shadow-md ring-1 ring-border ring-inset"
            >
              {items.map((item) => (
                <div key={item.id}>
                  {item.separated ? <div className="my-1 h-px bg-border" role="separator" /> : null}
                  <button
                    type="button"
                    role="menuitem"
                    disabled={item.disabled}
                    onClick={() => {
                      close();
                      item.onSelect();
                    }}
                    className={cn(
                      "flex w-full items-center gap-2.5 rounded-md px-2.5 py-2 text-left text-sm",
                      "hover:bg-muted focus-visible:bg-muted focus-visible:outline-none",
                      "disabled:pointer-events-none disabled:opacity-45",
                      "max-md:min-h-11",
                      item.destructive ? "text-danger" : "text-foreground",
                    )}
                  >
                    {item.icon ? (
                      <span
                        aria-hidden="true"
                        className={cn("shrink-0 [&_svg]:size-4", item.destructive ? "text-danger" : "text-muted-foreground")}
                      >
                        {item.icon}
                      </span>
                    ) : null}
                    <span className="min-w-0 flex-1">{item.label}</span>
                  </button>
                </div>
              ))}
            </div>,
            document.body,
          )
        : null}
    </span>
  );
}
